import { formatDateTime } from "../../../utils/formatters";
import React from "react";
import DataTable from "../../../components/ui/DataTable";
import { Card } from "../../../components/ui/Card";
import BatchRowActions from "../../batches/components/BatchRowActions";

/** Mobile card for a single dispatch history row — mirrors BatchCard's layout. */
const DispatchHistoryCard = ({ row, onDownload, downloadDisabled, onView }) => (
  <Card className="cursor-pointer px-4 py-3.5 transition-shadow hover:shadow-md" onClick={onView}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-900">{row.batchId}</p>
        <p className="truncate text-xs text-slate-500">To {row.dispatchedTo}</p>
      </div>
    </div>

    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-slate-400">Quantity</p>
        <p className="text-sm font-medium text-slate-700">{row.quantity ?? "-"}</p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-slate-400">Dispatched By</p>
        <p className="text-sm font-medium text-slate-700">{row.dispatchedBy}</p>
      </div>
      <div className="col-span-2">
        <p className="text-[11px] uppercase tracking-wide text-slate-400">Dispatched On</p>
        <p className="text-sm font-medium text-slate-700">{formatDateTime(row.dispatchedOn)}</p>
      </div>
    </div>

    <div className="mt-3 flex justify-end border-t border-slate-100 pt-2.5">
      <BatchRowActions onDownload={onDownload} downloadDisabled={downloadDisabled} onView={onView} />
    </div>
  </Card>
);

/**
 * Dispatch History table. Same column-driven `DataTable` shell as
 * `BatchTable`, same row-action icon set (via the shared `BatchRowActions`)
 * — download slip + view batch — so the two modules stay visually
 * identical per the brief, without duplicating table markup.
 */
const DispatchHistoryTable = ({ rows, isLoading, noDataLabel = "No dispatch history found", onRowAction }) => {
  const columns = [
    { key: "batchId", header: "Batch ID", render: (row) => <span className="font-medium text-slate-900">{row.batchId}</span> },
    { key: "dispatchedTo", header: "Dispatched To", render: (row) => row.dispatchedTo },
    { key: "dispatchedBy", header: "Dispatched By", render: (row) => row.dispatchedBy },
    { key: "dispatchedOn", header: "Dispatched On", render: (row) => formatDateTime(row.dispatchedOn) },
    { key: "quantity", header: "Quantity", render: (row) => row.quantity ?? "-" },
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <BatchRowActions
          downloadDisabled={!row.canDownloadSlip}
          onDownload={() => onRowAction("download", row)}
          onView={() => onRowAction("view", row)}
        />
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      loadingLabel="Loading dispatch history…"
      noDataLabel={noDataLabel}
      onRowClick={(row) => onRowAction("view", row)}
      renderCard={(row) => (
        <DispatchHistoryCard
          row={row}
          downloadDisabled={!row.canDownloadSlip}
          onDownload={() => onRowAction("download", row)}
          onView={() => onRowAction("view", row)}
        />
      )}
    />
  );
};

export default DispatchHistoryTable;
